// @mui
import React from 'react';

import { SxProps } from '@mui/material';
import { alpha, Theme, styled } from '@mui/material/styles';

// theme
import { ColorSchema } from '../theme/palette';

// ----------------------------------------------------------------------

type LabelColor = 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

type LabelVariant = 'filled' | 'outlined' | 'ghost';

interface OwnerState {
  color: LabelColor;
  variant: LabelVariant;
}

const RootStyle = styled('span', {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<{
  ownerState: OwnerState;
}>(({ theme, ownerState }) => {
  const { color, variant } = ownerState;
  const isLight = theme.palette.mode === 'light';

  const mainColor =
    color === 'default'
      ? theme.palette.grey[300]
      : (theme.palette[color as ColorSchema].main as string);
  const textContrast = theme.palette.getContrastText(mainColor);

  const ghostText =
    color === 'default'
      ? isLight
        ? theme.palette.grey[700]
        : theme.palette.grey[50]
      : (theme.palette[color as ColorSchema][isLight ? 'darker' : 'lighter'] as string);

  const filledStyles = { color: textContrast, backgroundColor: mainColor };
  const outlinedStyles = {
    color: mainColor,
    backgroundColor: 'transparent',
    border: `1px solid ${mainColor}`,
  };
  const ghostStyles = { color: ghostText, backgroundColor: alpha(mainColor, 0.24) };
  const variantStyle =
    variant === 'filled' ? filledStyles : variant === 'outlined' ? outlinedStyles : ghostStyles;

  return {
    height: 24,
    minWidth: 24,
    lineHeight: 0,
    borderRadius: 6,
    cursor: 'default',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    padding: theme.spacing(0, 1),
    fontSize: theme.typography.pxToRem(12),
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold,
    ...variantStyle,
  };
});

interface Props {
  color?: LabelColor;
  variant?: LabelVariant;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
}

export default function Label({
  color = 'default',
  variant = 'ghost',
  children,
  sx,
  ...other
}: Props) {
  return (
    <RootStyle ownerState={{ color, variant }} sx={sx} {...other}>
      {children}
    </RootStyle>
  );
}
